<script lang="ts">
  import NewsletterCard from './newsletter-card.svelte';
  import type { ConfigurationStoryblok } from '$types/bloks';
  import Link from './ui/link.svelte';
  import { getAnchorFromCmsLink, getImageAttributes } from '$lib/utils/cms';
  import Accordion from './accordion.svelte';
  import Logo from './logo.svelte';
  import LangSwitcher from './lang-switcher.svelte';
  import { region } from '$lib/region';
  import StoreSwitcher from './store-switcher.svelte';
  import { buildSrcSet } from '$lib/image';
  import { showLangSwitcher } from '$lib/country';
  import { cn } from '$lib/utils';

  export let configuration: ConfigurationStoryblok;

  let className: string | undefined = undefined;
  export { className as class };

  $: link = configuration.image_link ? $getAnchorFromCmsLink(configuration.image_link) : null;
</script>

<footer class={className}>
  <div class="px-2 py-4 lg:p-8">
    <div class="container mx-auto flex flex-col md:flex-row w-full gap-2 lg:gap-4">
      <NewsletterCard
        title={configuration.footer_newsletter_cta_title}
        description={configuration.footer_newsletter_cta_description}
        placeholder={configuration.footer_newsletter_cta_placeholder}
        button={configuration.footer_newsletter_cta_button}
        class="w-full"
      />

      <Link
        class={cn(
          'h-[138px] md:h-[180px] w-full overflow-hidden rounded-sm object-cover bg-cover center',
          (!link || !link.href) && 'pointer-events-none'
        )}
        href={link?.href}
        target={link?.target}
      >
        <img
          loading="lazy"
          src={getImageAttributes(configuration.footer_newsletter_image, { size: [400, 0] }).src}
          alt="newsletter"
          srcset={buildSrcSet({
            loader: ({ width }) =>
              getImageAttributes(configuration.footer_newsletter_image, { size: [width, 0] }).src,
            widths: [(w) => w]
          })}
          sizes="100vw"
          class="object-cover xl:object-contain h-full xl:h-auto w-full xl:w-auto"
        />
      </Link>
    </div>
  </div>
  <div
    class="flex flex-col gap-10 lg:flex-row-reverse lg:justify-between lg:border-y lg:border-primary/4 lg:p-8"
  >
    <div class="container mx-auto flex">
      <!-- Mobile Nav-->
      <div class="lg:hidden w-full">
        {#if configuration.footer_navigation}
          {#each configuration.footer_navigation as link}
            {#if link.group}
              <Accordion title={link.title || ''} variant="footer" group="footer">
                <div class="my-0.5 flex flex-col gap-5">
                  {#each link.group as group_item}
                    {#if group_item.component === 'regional-link'}
                      {@const links = {
                        us: group_item.link_us
                          ? $getAnchorFromCmsLink(group_item.link_us)
                          : undefined,
                        uk: group_item.link_uk
                          ? $getAnchorFromCmsLink(group_item.link_uk)
                          : undefined,
                        eu: group_item.link_eu
                          ? $getAnchorFromCmsLink(group_item.link_eu)
                          : undefined
                      }}
                      <Link {...links[$region]}>{group_item.text}</Link>
                    {:else if group_item.url}
                      <Link {...$getAnchorFromCmsLink(group_item.url)} class="font-medium"
                        >{group_item?.text}</Link
                      >
                    {/if}
                  {/each}
                </div>
              </Accordion>
            {/if}
          {/each}
        {/if}
      </div>
      <!-- Desktop Nav -->
      <div class="hidden gap-4 flex-wrap lg:flex w-full">
        <div class="flex-1 w-full max-w-md">
          <Logo aria-hidden="true" aria-label="Hey Harper" />
        </div>
        {#if configuration.footer_navigation}
          <div class="flex w-fit gap-14">
            {#each configuration.footer_navigation as link}
              {#if link.group}
                <div class="flex flex-col gap-5 lg:gap-3">
                  <p class="font-semibold text-primary">{link.title}</p>
                  {#each link.group as item}
                    <div class="flex flex-col">
                      {#if item.component === 'regional-link'}
                        {@const links = {
                          us: item.link_us ? $getAnchorFromCmsLink(item.link_us) : undefined,
                          uk: item.link_uk ? $getAnchorFromCmsLink(item.link_uk) : undefined,
                          eu: item.link_eu ? $getAnchorFromCmsLink(item.link_eu) : undefined
                        }}
                        <Link {...links[$region]}>{item.text}</Link>
                      {:else if item.url}
                        <Link {...$getAnchorFromCmsLink(item.url)}>{item.text}</Link>
                      {/if}
                    </div>
                  {/each}
                </div>
              {/if}
            {/each}
          </div>
        {/if}
      </div>
    </div>
  </div>
  <div class="px-4 pt-8 pb-[64px] lg:px-8 lg:py-[18px]">
    <div class="container mx-auto flex flex-col lg:flex-row lg:justify-between lg:items-center">
      <div
        class="flex justify-between pb-4 flex-col-reverse lg:flex-row lg:items-center lg:justify-start lg:pb-0 w-full lg:w-fit gap-6 lg:gap-5"
      >
        <div class="flex justify-between lg:justify-start gap-6 lg:gap-5">
          <div class="flex items-center">
            <StoreSwitcher />
          </div>
          {#if $showLangSwitcher}
            <div class="flex gap-2 flex-col items-end">
              <LangSwitcher />
            </div>
          {/if}
        </div>
        {#if configuration.payment_methods}
          <div class="flex gap-1.5">
            {#each configuration.payment_methods as payment_method}
              <img
                src={payment_method.filename}
                alt={payment_method.alt}
                class="w-[35px] lg:w-[29px]"
                width="35"
                height="24"
              />
            {/each}
          </div>
        {/if}
      </div>
      <div class="flex gap-2 lg:items-center">
        {#if configuration.footer_legal}
          {#each configuration.footer_legal as legal, index}
            {#if legal.url}
              <Link {...$getAnchorFromCmsLink(legal.url)} class="text-primary/60 text-sm w-fit"
                >{legal?.text}</Link
              >
            {/if}
            {#if index !== configuration.footer_legal.length - 1}
              <p class="text-primary/60 text-sm">·</p>
            {/if}
          {/each}
        {/if}
      </div>
    </div>
  </div>
</footer>
